import React from 'react';
import { shallowEqual } from 'react-redux';
import { Object3D } from 'three';
import * as THREE from 'three';
import Engine from './Engine';
import Scenario from "./scenarioElements/Scenario";
import Input from './Input';
import VisibilityManager from './VisibilityManager';
import ProjectSaver from './ProjectSaver';
import ARTracker from './ARTracker';
import ActionManager from './ActionManager';
import WorkspaceManager from './WorkspaceManager';
import MaterialAsset from './MaterialAsset';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import ProjectPacker from './ProjectPacker';
import WorkspaceUtils from './utilities/WorkspaceUtils';
import AnalyticsManager from './AnalyticsManager';
import SessionUtils from './utilities/SessionUtils';
import SelectObjectsAction from '../actions/SelectObjectsAction';
import { useTypedDispatch, useTypedSelector } from '../state/hooks';
import { ApplicationModuleTypes, CustomEventTypes, SimulationStates } from '../globals';
import store, { StoreActions } from '../state/store';
import PasteLinkedObjectsAction from '../actions/PasteLinkedObjectsAction';
import PasteObjectsAction from '../actions/PasteObjectsAction';
import RemoveObjectAction from '../actions/RemoveObjectAction';
import ModalManager from '../components/layout/ModalManager';
import OpenProjectDialog from '../components/welcome/OpenProjectDialog';
import SaveWorkspaceDialog from '../components/workspace/SaveWorkspaceDialog';
import DownloadScenarioDialog from '../components/publishing/DownloadScenarioDialog';
import PublishScenarioDialog from '../components/publishing/PublishScenarioDialog';
import ScenarioShareDialog from '../components/ScenarioShareDialog';
import { FeatureFlagNames } from '../interfaces/enums';
import { ProjectStates } from '../interfaces/ProjectStates';
import { IProjectPermissions } from '../state/slices/ScenarioSlice';
import DeleteProjectDialog from '../components/welcome/DeleteProjectDialog';
import useCurrentScenario from '../components/reactUtils/useCurrentScenario';
import useEventListener from '../components/reactUtils/useEventListener';
import { IModalClosedEventDetails } from '../interfaces/EventDetails';
import ScenarioControls from '../components/applicationTopBar/ScenarioControls';

export const sourceClipboardObjects = new Array<Object3D>();
export const clipboard = new Array<Object3D>();

export function copyObjects(fillClipboard: boolean)
{
	const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects as Array<Object3D>;

	if (selectedObjects.length > 0)
	{
		if (fillClipboard) clipboard.length = 0;

		sourceClipboardObjects.length = 0;

		for (const object of selectedObjects)
		{
			let parentIsSelected = false;
			for (const parent of selectedObjects)
			{
				if (SceneObjectUtils.isDescendantOf(object, parent))
				{
					parentIsSelected = true;
					break;
				}
			}
			if (!parentIsSelected)
			{
				sourceClipboardObjects.push(object);
			}
		}
		const destination: any[] = fillClipboard ? clipboard : [];

		const currentStep = Scenario.currentStep();
		currentStep?.timeline?.resetAnimations();

		for (const objectToCopy of sourceClipboardObjects)
		{
			const clonedObject = SceneObjectUtils.cloneObject(objectToCopy);
			objectToCopy.updateWorldMatrix(true, false);
			objectToCopy.matrixWorld.decompose(clonedObject.position, clonedObject.quaternion, clonedObject.scale);
			//the world scale of sprites does not represent their actual scene scale. it gets forced to be a certian number within updateMatrixWorld. use local instead.
			if (objectToCopy instanceof THREE.Sprite) clonedObject.scale.copy(objectToCopy.scale);

			destination.push(clonedObject);
		}

		currentStep?.timeline?.update();

		return destination;
	}
}

export function pasteObjects(pasteAsLinkedObjects = false): void
{
	const newObjects = new Array<Object3D>();
	for (const clipboardObject of clipboard)
	{
		newObjects.push(SceneObjectUtils.cloneObject(clipboardObject));
	}

	if(pasteAsLinkedObjects)
	{
		new PasteLinkedObjectsAction([...sourceClipboardObjects], newObjects);
	}
	else
	{
		const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
		let parent = selectedObjects[0]?.parent ?? undefined;
		for (const selectedObject of selectedObjects)
		{
			if (selectedObject.parent !== parent)
			{
				parent = undefined;
				break;
			}
		}

		new PasteObjectsAction(newObjects, parent, newObjects.length > 1 ? "Pasted Objects" : "Pasted Object");
		ProjectSaver.getInstance().onPaste(newObjects, sourceClipboardObjects);
	}
}

export function duplicateObjects(keepParents = false): void
{
	const objectCopies = copyObjects(false);
	if (objectCopies?.length)
	{
		const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
		const parents = new Array(selectedObjects.length);

		if(keepParents)
		{
			for(let i = 0; i < selectedObjects.length; i++)
			{
				parents[i] = selectedObjects[i].parent;
			}
		}
		else
		{
			parents.fill(selectedObjects[0].parent);
			for (const selectedObject of selectedObjects)
			{
				if (selectedObject.parent !== parents[0])
				{
					parents.fill(null);
					break;
				}
			}
		}

		new PasteObjectsAction(objectCopies, parents, objectCopies.length > 1 ? "Duplicated Objects" : "Duplicated Object");
		ProjectSaver.getInstance().onPaste(objectCopies, sourceClipboardObjects);
	}
}

export function deleteObjects(): void
{
	const objects = new Array<Object3D>();
	let trackersCount = 0;

	for (const object of Engine.getInstance().getPickingManager().getCurrentSelection().objects)
	{
		if (object && object instanceof ARTracker && ARTracker.getTrackerList().length <= 1) continue;
		if (object && object instanceof ARTracker ) trackersCount++;
		objects.push(object);
	}

	if (trackersCount === ARTracker.getTrackerList().length) return;

	new RemoveObjectAction(objects);
}

export function openWindow(module: ApplicationModuleTypes): void
{
	AnalyticsManager.managedEvent('windowVisibility', 'windowId', module);
	WorkspaceManager.openWindow(module);
}

export function toggleWindow(module: ApplicationModuleTypes): void
{
	if(WorkspaceManager.findTabByID(module) && store.getState().workspaceModule.activeTab === module)
	{
		WorkspaceManager.closeWindow(module);
	}
	else
	{
		openWindow(module);
	}
}

export function cutObjects(): void
{
	copyObjects(true);

	const objects = new Array<Object3D>();
	let trackersCount = 0;
	for (const object of Engine.getInstance().getPickingManager().getCurrentSelection().objects)
	{
		if (object && object instanceof ARTracker && ARTracker.getTrackerList().length <= 1) continue;
		if (object && object instanceof ARTracker ) trackersCount++;
		objects.push(object);
	}

	if (trackersCount === ARTracker.getTrackerList().length) return;

	new RemoveObjectAction(objects, objects.length > 0 ? objects.length > 1 ? "Cut Objects" : "Cut " + objects[0].name : "");
}

export function selectAll(): void
{
	const currentSelection = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
	const objectsToSelect = new Array<Object3D>();
	const objectsToDeselect = new Array<Object3D>();
	for (const child of Engine.getInstance().getSelectionRoot().children)
	{
		if (!child.userData.hideInHierarchy && !currentSelection.includes(child))
		{objectsToSelect.push(child);}
	}
	for (const child of currentSelection)
	{
		if (child.parent !== Engine.getInstance().getSelectionRoot())
		{objectsToDeselect.push(child);}
	}
	new SelectObjectsAction(objectsToSelect, objectsToDeselect);
}

export function selectAllMeshObjects(): void
{
	const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
	const objectsToSelect = new Array<Object3D>();
	const objectsToDeselect = new Array<Object3D>();

	if(selectedObjects.length)
	{
		for(const selectedObject of selectedObjects)
		{
			if(!MaterialAsset.getMaterialAssetByObjectGuid(selectedObject.userData.guid))
			{
				objectsToDeselect.push(selectedObject);
			}

			selectedObject.traverse((child) =>
			{
				if (!selectedObjects.includes(child) && MaterialAsset.getMaterialAssetByObjectGuid(child.userData.guid))
				{
					objectsToSelect.push(child);
				}
			});
		}
	}
	else
	{
		Engine.getInstance().getSelectionRoot().traverse((child) =>
		{
			if (!selectedObjects.includes(child) && MaterialAsset.getMaterialAssetByObjectGuid(child.userData.guid))
			{
				objectsToSelect.push(child);
			}
		});
	}

	new SelectObjectsAction(objectsToSelect, objectsToDeselect);
}

export function selectAllHiddenObjects(): void
{
	const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
	const objectsToSelect = new Array<Object3D>();
	const objectsToDeselect = new Array<Object3D>();

	const hiddenObjects = VisibilityManager.getHiddenObjects();
	const rootObject = Engine.getInstance().getSelectionRoot();

	rootObject.traverse((child) =>
	{
		if(child.userData.hideInHierarchy) return;
		if (hiddenObjects.includes(child) && !selectedObjects.includes(child))
		{
			objectsToSelect.push(child);
		}
		else if(selectedObjects.includes(child))
		{
			objectsToDeselect.push(child);
		}
	});

	new SelectObjectsAction(objectsToSelect, objectsToDeselect);
}

export function selectAllWithMaterial(materialId: string): void
{
	const currentSelection = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
	const objectsToSelect = new Set<Object3D>();
	const objectsToDeselect = new Set<Object3D>();

	Engine.getInstance().getSelectionRoot().traverse(child =>
	{
		const objectMaterial = MaterialAsset.getMaterialAssetByObjectGuid(child.userData.guid);
		if (!currentSelection.includes(child) && objectMaterial && objectMaterial.guid === materialId)
		{objectsToSelect.add(child);}
	});

	for (const selected of currentSelection)
	{
		const objectMaterial = MaterialAsset.getMaterialAssetByObjectGuid(selected.userData.guid);
		if (!objectMaterial || objectMaterial.guid !== materialId)
		{objectsToDeselect.add(selected);}
	}
	new SelectObjectsAction(Array.from(objectsToSelect), Array.from(objectsToDeselect));
}

export function deselectAll(): void
{
	new SelectObjectsAction([], Engine.getInstance().getPickingManager().getCurrentSelection().objects);
}

const inputAcceptingModules = [
	ApplicationModuleTypes.SceneView,
	ApplicationModuleTypes.ObjectGroups,
	ApplicationModuleTypes.StepHierarchy,
	ApplicationModuleTypes.ARTrackers,
]



export function checkShortcutInput(): void
{
	// viewers get NOTHING here
	if(store.getState().applicationModule?.isViewOnlyMode) return;

	// scene-related
	const focusedWindow = store.getState().workspaceModule.activeWindow;
	if (!WorkspaceUtils.shouldAllowNativeBehavior() && focusedWindow && WorkspaceUtils.isModuleInputAccepting(focusedWindow, inputAcceptingModules))
	{
		if (Input.getKeybindDown(Input.KeyBindings.Delete))
		{
			deleteObjects();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Copy))
		{
			copyObjects(true);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Paste))
		{
			pasteObjects();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Duplicate))
		{
			duplicateObjects();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Cut))
		{
			cutObjects();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.SelectAll))
		{
			selectAll();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.DeselectAll))
		{
			deselectAll();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.IsolateVisibility))
		{
			VisibilityManager.isolateObjects(Engine.getInstance().getPickingManager().getCurrentSelection().objects);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.HideVisibility))
		{
			VisibilityManager.hideObjects(Engine.getInstance().getPickingManager().getCurrentSelection().objects, true);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.HideVisibilityNoneRecursive))
		{
			VisibilityManager.hideObjects(Engine.getInstance().getPickingManager().getCurrentSelection().objects, false);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UnhideVisibility))
		{
			VisibilityManager.restoreObjects([]);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UnhideSelectedVisibility))
		{
			VisibilityManager.restoreObjects(Engine.getInstance().getPickingManager().getCurrentSelection().objects);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UIToggleVisibility))
		{
			store.dispatch(StoreActions.ui.setUIPreviewVisible({ value: !store.getState().uiModule.previewVisible }));
		}
	}

	// general
	if (!WorkspaceUtils.isModalOpen())
	{
		if (Input.getKeybindDown(Input.KeyBindings.Redo) || Input.getKeybindDown(Input.KeyBindings.Redo2))
		{
			ActionManager.redo();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Undo))
		{
			ActionManager.undo();
		}
	}
}

export function ReactShortcutHandler()
{
	const dispatch = useTypedDispatch();
	const currentScenario = useCurrentScenario();

	const permissions = useTypedSelector<IProjectPermissions>(state => state.scenarioModule.permissions, shallowEqual);
	const isPublishInProgress = useTypedSelector(state => state.applicationModule.isPublishInProgress, shallowEqual);
	const isViewOnlyMode = useTypedSelector<boolean>(state => state.applicationModule.isViewOnlyMode, shallowEqual);
	const projectState = useTypedSelector<ProjectStates>(state => state.scenarioModule.projectState, shallowEqual);
	const [openProjectModalId, setOpenProjectModalId] = React.useState<string>("");
	const [publishModalId, setPublishModalId] = React.useState<string>("");
	const [shareModalId, setShareModalId] = React.useState<string>("");
	const [downlaodModalId, setDownloadModalId] = React.useState<string>("");
	const [saveWorkspaceModalId, setSaveWorkspaceModalId] = React.useState<string>("");
	const [deleteProjectModalId, setDeleteProjectModalId] = React.useState<string>("");

	useEventListener(CustomEventTypes.onModalClosed, (e: IModalClosedEventDetails) => 
	{
		if(!e.detail?.modalIds) return;
		if (e.detail.modalIds.includes(openProjectModalId)) setOpenProjectModalId("");
		if (e.detail.modalIds.includes(publishModalId)) setPublishModalId("");
		if (e.detail.modalIds.includes(shareModalId)) setShareModalId("");
		if (e.detail.modalIds.includes(downlaodModalId)) setDownloadModalId("");
		if (e.detail.modalIds.includes(saveWorkspaceModalId)) setSaveWorkspaceModalId("");
		if (e.detail.modalIds.includes(deleteProjectModalId)) setDeleteProjectModalId("");
	}, document);
	
	useEventListener(CustomEventTypes.onInputUpdated, () =>
	{
		if(isViewOnlyMode)
		{
			if (Input.getKeybindDown(Input.KeyBindings.Open) && !openProjectModalId)
			{				
				setOpenProjectModalId(ModalManager.addModal({ content: <OpenProjectDialog /> }));
			}

			//authors/reviewers can publish as long as project is not archived
			if(projectState !== ProjectStates.Archived && (permissions.canUpdate || permissions.canReview))
			{
				if (Input.getKeybindDown(Input.KeyBindings.Publish))
				{
					if(!isPublishInProgress && !publishModalId)
					{
						setPublishModalId(ModalManager.addModal({ content: <PublishScenarioDialog /> }));
					}
				}
			}
			return;
		}

		//File Menu
		if (Input.getKeybindDown(Input.KeyBindings.Save))
		{
			ProjectPacker.getInstance().saveProject();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.SaveAs))
		{
			ProjectPacker.getInstance().saveProject(true);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.NewRevision))
		{
			ScenarioControls.onNewRevision();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Open) && !openProjectModalId)
		{
			dispatch(StoreActions.application.setSimulationState(SimulationStates.Stopped));
			setOpenProjectModalId(ModalManager.addModal({ content: <OpenProjectDialog /> }));
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Publish))
		{
			if(!isPublishInProgress && !publishModalId)
			{
				setPublishModalId(ModalManager.addModal({ content: <PublishScenarioDialog /> }));
			}
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Share))
		{
			if(!isPublishInProgress && !shareModalId && permissions.canManage)
			{
				setShareModalId(ModalManager.addModal({ content: <ScenarioShareDialog /> }));
			}
		}
		// PUT REVISION HERE
		else if (Input.getKeybindDown(Input.KeyBindings.DownloadScenario) && !downlaodModalId)
		{
			setDownloadModalId(ModalManager.addModal({ content: <DownloadScenarioDialog /> }));
		}
		else if (Input.getKeybindDown(Input.KeyBindings.DeleteProject) && permissions.canDelete && !isPublishInProgress && !deleteProjectModalId)
		{
			if(!currentScenario) return;
			const revisions = store.getState().scenarioModule.revisions;
			const revisionName = store.getState().scenarioModule.currentRevision?.revisionName;
			setDeleteProjectModalId(ModalManager.addModal({content: <DeleteProjectDialog projectId={currentScenario.id} projectName={currentScenario.name} revisionProjectIds={revisions.map(r => r.projectId)} revisionName={revisionName}/>}))
		}
		//Window Menu
		else if (Input.getKeybindDown(Input.KeyBindings.AssetLibrary))
		{
			toggleWindow(ApplicationModuleTypes.AssetLibrary);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.ObjectGroups))
		{
			toggleWindow(ApplicationModuleTypes.ObjectGroups);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.RevisionsEditor))
		{
			toggleWindow(ApplicationModuleTypes.RevisionsEditor);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.SequenceEditor))
		{
			toggleWindow(ApplicationModuleTypes.SequenceEditor);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.TimelineEditor))
		{
			toggleWindow(ApplicationModuleTypes.TimelineEditor);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.VisageEditor))
		{
			toggleWindow(ApplicationModuleTypes.VisageEditorWindow);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UIEditor))
		{
			toggleWindow(ApplicationModuleTypes.UIEditor);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.StepHierarchy))
		{
			toggleWindow(ApplicationModuleTypes.StepHierarchy);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.ChecklistEditor))
		{
			toggleWindow(ApplicationModuleTypes.FormEditor);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.ARTrackers))
		{
			toggleWindow(ApplicationModuleTypes.ARTrackers);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.ObjectProperties))
		{
			toggleWindow(ApplicationModuleTypes.ObjectProperties);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.ActionHistory))
		{
			toggleWindow(ApplicationModuleTypes.ActionHistory);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.IoT))
		{
			if(SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.connectedServices))
				toggleWindow(ApplicationModuleTypes.IoT);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.StepConditions))
		{
			if(SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.connectedServices))
				toggleWindow(ApplicationModuleTypes.StepConditions);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.StepActions))
		{
			toggleWindow(ApplicationModuleTypes.StepActions);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.ProjectHealth))
		{
			toggleWindow(ApplicationModuleTypes.ProjectHealth);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.Attachments))
		{
			toggleWindow(ApplicationModuleTypes.Attachments);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.PartsCatalogEditor))
		{
			if(SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.partsCatalog))
				toggleWindow(ApplicationModuleTypes.PartsCatalogEditor);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.CloseAll))
		{
			WorkspaceManager.closeAllWindows();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.AddSceneView))
		{
			WorkspaceManager.addSceneView();
		}
		else if (Input.getKeybindDown(Input.KeyBindings.SaveCustomWorkspace) && !saveWorkspaceModalId)
		{
			setSaveWorkspaceModalId(ModalManager.addModal({ content: <SaveWorkspaceDialog /> }));
		}
		else if (Input.getKeybindDown(Input.KeyBindings.FactoryPreset0))
		{
			WorkspaceManager.loadLayoutPresetByIdx(0, true);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.FactoryPreset1))
		{
			WorkspaceManager.loadLayoutPresetByIdx(1, true);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.FactoryPreset2))
		{
			WorkspaceManager.loadLayoutPresetByIdx(2, true);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.FactoryPreset3))
		{
			WorkspaceManager.loadLayoutPresetByIdx(3, true);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UserDefinedLayout0))
		{
			WorkspaceManager.loadLayoutPresetByIdx(0, false);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UserDefinedLayout1))
		{
			WorkspaceManager.loadLayoutPresetByIdx(1, false);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UserDefinedLayout2))
		{
			WorkspaceManager.loadLayoutPresetByIdx(2, false);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UserDefinedLayout3))
		{
			WorkspaceManager.loadLayoutPresetByIdx(3, false);
		}
		else if (Input.getKeybindDown(Input.KeyBindings.UserDefinedLayout4))
		{
			WorkspaceManager.loadLayoutPresetByIdx(4, false);
		}

	}, document);

	return null;
}
